import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { object, string } from 'yup';
import { useFormik } from 'formik';
import C from 'classnames';

import S from './OrganizationsView.module.scss';

// Store
import { Dispatch, RootState } from '../../store/store';

// Constants
import { TEXT_LENGTH_LIMITS } from '../../constants/common';

// Components
import InputControl from '../../components/InputControl/InputControl';
import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';
import SpinnerLoader from '../../components/SpinnerLoader/SpinnerLoader';
import { UpdateOrganizationFormValues } from './types';
import Switcher, { ResponseSwitch } from '../../components/Switcher/Switcher';
import CycleMeasurementSymbol from '../../enums/cycle-measurement-symbol';

type OrganizationsViewProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const OrganizationsView: React.FC<OrganizationsViewProps> = ({
  organization,
  getOrganization,
  updateOrganization,
  uploadLogo,
  deleteLogo,
  getCurrentUser,
}) => {
  const [isOrganizationLoaded, setIsOrganizationLoaded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const filterRef = useRef(null);

  const formik = useFormik<UpdateOrganizationFormValues>({
    initialValues: {
      name: organization?.name ?? '',
      isCelsius: organization?.isCelsius ?? false,
      file: null,
    },
    validationSchema: object({
      name: string()
        .required('Required')
        .min(TEXT_LENGTH_LIMITS.MIN, `Min name length is ${TEXT_LENGTH_LIMITS.MIN} characters`)
        .max(TEXT_LENGTH_LIMITS.MAX, `Max name length is ${TEXT_LENGTH_LIMITS.MAX} characters`),
    }),
    onSubmit: async (values) => {
      const { name, isCelsius } = values;
      await updateOrganization({ name, isCelsius });
    },
  });

  const isDisabledBtn = Boolean(formik.touched.name && formik.errors.name);

  const fetchOrganizations = async () => {
    const response = await getOrganization();
    const organizationData = response?.organization;
    if (organizationData) {
      Object.entries(organizationData).forEach(([key, value]) => {
        formik.handleChange({
          target: {
            name: key,
            value: value,
          },
        });
      });
    }

    setIsOrganizationLoaded(true);
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setIsImageLoaded(false);
      setIsUploading(true);
      const { files } = event.target;
      await uploadLogo(files[0]);
      await fetchOrganizations();
      await getCurrentUser();
    } catch (error) {
      console.error(error);
    } finally {
      filterRef.current.value = '';
      setIsUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      setIsUploading(true);
      await deleteLogo();
      await fetchOrganizations();
      await getCurrentUser();
    } catch (error) {
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  const onUnitsChange = (data: ResponseSwitch) => {
    formik.setFieldValue('isCelsius', data.value);
  };

  return (
    <>
      {!isOrganizationLoaded && <div className="flex items-center justify-center">Loading...</div>}
      {isOrganizationLoaded && (
        <div className="p-organizations-view">
          <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
            <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
              <h3 className="font-medium text-black dark:text-white">Organization Settings</h3>
            </div>
            <form className="flex flex-col gap-5.5 p-7" onSubmit={formik.handleSubmit}>
              <div className="w-full">
                <InputControl
                  label="Organization Name"
                  type="text"
                  placeholder="Type your organization name"
                  name="name"
                  value={formik.values.name}
                  customOnChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  showError={formik.touched.name && formik.errors.name}
                  errorText={formik.errors.name}
                />
              </div>

              <input type="file" onChange={handleFileChange} ref={filterRef} id="upload-org-logo" hidden />

              {!organization?.logo && (
                <label htmlFor="upload-org-logo" className={S.uploadLogoButton}>
                  {isUploading ? 'Uploading...' : 'Upload Logo'}
                  {isUploading && (
                    <div className={S.spinnerRoot}>
                      <SpinnerLoader />
                    </div>
                  )}
                </label>
              )}

              {organization?.logo && (
                <div className={S.imageWrapper}>
                  <div className={S.deleteImage} onClick={handleDeleteLogo}>
                    <span className={S.cross}>x</span>
                  </div>
                  <label htmlFor="upload-org-logo" className={C(S.labelWrapper, { [S.loaded]: isImageLoaded })}>
                    <img
                      src={organization.logo}
                      alt={organization.name}
                      className={S.image}
                      onLoad={() => setIsImageLoaded(true)}
                    />
                    {isUploading && (
                      <div className={S.spinnerRoot}>
                        <SpinnerLoader />
                      </div>
                    )}
                  </label>
                </div>
              )}

              <div className="c-input-control__input-wrapper">
                <p className="flex justify-between py-2.5 font-medium text-black dark:text-white">Organization Units</p>
                <Switcher
                  options={{
                    left: CycleMeasurementSymbol.Celsius,
                    right: CycleMeasurementSymbol.Fahrenheit,
                  }}
                  isEnable={formik.values.isCelsius}
                  onChange={onUnitsChange}
                  switcherId={`${formik.values.name}-org-units-id`}
                />
                <div className="flex min-h-6 items-center justify-start"></div>
              </div>
              <div className="flex justify-end gap-4.5">
                <InteractiveButton text="Update" submit disabled={isDisabledBtn} isLoading={formik.isSubmitting} />
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

const mapState = (state: RootState) => ({
  organization: state.organizations.organization,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getOrganization: dispatch.organizations.getOrganization,
  updateOrganization: dispatch.organizations.updateOrganization,
  uploadLogo: dispatch.organizations.uploadLogo,
  deleteLogo: dispatch.organizations.deleteLogo,
  getCurrentUser: dispatch.user.getCurrentUser,
});

export default connect(mapState, mapDispatch)(OrganizationsView);
